import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Typography, useMediaQuery, useTheme } from "@mui/material";
import Stack from "@mui/material/Stack";
import moment from "moment";
import { useEffect, useMemo } from "react";
import { useCompany } from "../../../../Shared/components/CompanyProvider/CompanyProvider";
import { Tokens } from "../../../../Shared/sharedTypes/Tokens";
import { setDayFilterSliderSettings } from "../../../../Shared/utils/sliderSettings";
import { tokenReplace } from "../../../../Shared/utils/tokenReplacement";
import { useHelpers } from "../../../hooks/useHelpers";
import { ActionTypes } from "../../../OrderFlowProvider/OrderFlowActionTypes";
import { useOrderFlow } from "../../../OrderFlowProvider/OrderFlowContext";
import { DEFAULT_DAY_COUNT, ModuleNames } from "../../../OrderFlowProvider/OrderFlowTypes";
import { setMealsSkippedForDay } from "../../../OrderFlowProvider/reducerUtils";
const DayFilterText = {
    DEFAULT_SKIP_BUTTON_TEXT: `${Tokens.Action} ${Tokens.MealEntityNamePlural} for ${Tokens.Day}`,
    ADD: "Add",
    SKIP: "Skip",
};
const handleSelectedDayChange = (selectedDay, dispatch) => (event) => {
    const day = moment(event.currentTarget.value);
    if (moment(selectedDay).isSame(day)) {
        return;
    }
    dispatch({ type: ActionTypes.SET_SELECTED_DAY, payload: day });
};
const CreateDayFormattedLabel = (day, dayFormat, checkmarkElement) => {
    if (dayFormat && dayFormat.length) {
        return (_jsxs(Stack, Object.assign({ alignItems: "center" }, { children: [dayFormat.slice(0, -1).map((formatString) => (_jsx(Typography, { children: moment(day).format(formatString) }, formatString))), _jsxs(Stack, Object.assign({ direction: "row", alignItems: "center", spacing: 1 }, { children: [_jsx(Typography, { children: moment(day).format(dayFormat[dayFormat.length - 1]) }), checkmarkElement] }))] })));
    }
    else {
        return (_jsxs(Stack, Object.assign({ direction: "row", alignItems: "center", spacing: 1 }, { children: [_jsx(Typography, { children: moment(day).format("MMM D") }), checkmarkElement] })));
    }
};
const toggleDaySkipped = (dispatch, meals, skip) => (event) => {
    const day = moment(event.currentTarget.value);
    setMealsSkippedForDay(dispatch, meals, day, !skip);
};
const createSkipDayButtonText = (config, isMobile, isSkipped, selectedDay, tokenMap) => {
    const { SkipDayButtonTextMobile, SkipDayButtonDateFormatMobile, SkipDayButtonTextDesktop, SkipDayButtonDateFormatDesktop, } = config;
    let baseText = isMobile ? SkipDayButtonTextMobile : SkipDayButtonTextDesktop;
    if (!baseText || baseText === "") {
        baseText = DayFilterText.DEFAULT_SKIP_BUTTON_TEXT;
    }
    const action = isSkipped ? DayFilterText.ADD : DayFilterText.SKIP;
    const dateFormat = isMobile ? SkipDayButtonDateFormatMobile : SkipDayButtonDateFormatDesktop;
    return tokenReplace(baseText.replace(Tokens.Action, action).replace(Tokens.Day, selectedDay.format(dateFormat !== null && dateFormat !== void 0 ? dateFormat : "dddd")), true, tokenMap);
};
const isDayCompleted = (meals) => (day) => {
    // Get all meals for this specific day
    const mealsForDay = meals.filter((meal) => moment(meal.MealDate).isSame(day, "day"));
    // If there are no meals for this day, it's not completed
    if (mealsForDay.length === 0) {
        return false;
    }
    // Calculate total meal quantity and total assigned recipes for the day
    const totalMealQuantity = mealsForDay.reduce((sum, meal) => sum + meal.Quantity, 0);
    // If there are no meals with quantity, it's not completed
    if (totalMealQuantity === 0) {
        return false;
    }
    // Count total recipe assignments for this day
    const totalAssignedRecipes = mealsForDay.reduce((sum, meal) => {
        if (!meal.RecipeAssignments) {
            return sum;
        }
        return sum + meal.RecipeAssignments.reduce((assignmentSum, assignment) => assignmentSum + assignment.Quantity, 0);
    }, 0);
    // Day is completed if all meals have recipe assignments
    return totalAssignedRecipes >= totalMealQuantity;
};
/**
 * This currently treats a day as "skipped" if ANY meal on that day is skipped.
 * Once individual meal skipping is fully supported, this should instead verify that
 * ALL meals for the selected day are skipped.
 */
export const isAnyMealSkippedForGivenDay = (meals) => (day) => {
    return meals.some((meal) => moment(meal.MealDate).isSame(day, "day") && meal.IsSkipped);
};
export const useDayFilter = (sequenceNumber) => {
    var _a, _b, _c, _d;
    const { store, dispatch } = useOrderFlow();
    const theme = useTheme();
    const { tokenMap } = useCompany();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
    const { getDayFilterDays } = useHelpers();
    const moduleConfig = (_a = store.model.Flow.FlowModules.find((module) => module.ModuleName === ModuleNames.DAY_FILTER &&
        module.Sequence === sequenceNumber &&
        module.Page === store.cart.currentPage)) === null || _a === void 0 ? void 0 : _a.ModuleConfig;
    const onlyDaysWithDefaultMeals = moduleConfig.OnlyDaysWithDefaultMeals;
    const mealSelectionStatus = (_b = store.model.MealSelectionStatus) === null || _b === void 0 ? void 0 : _b.completed;
    const dayCount = (_c = moduleConfig.DayCount) !== null && _c !== void 0 ? _c : DEFAULT_DAY_COUNT;
    const dayFilterDays = useMemo(() => getDayFilterDays(store.cart.daysOfWeek, store.model.Recipes, store.model.DeliveryDays, store.cart.startDate, store.cart.isSubscription, store.cart.subscriptionPeriod, store.cart.oneTimeOrderPeriod, moduleConfig.OnlySelectedDaysOfWeek, moduleConfig.OnlyDaysWithDefaultMeals, store.model.GeneralSettings.OnlyShowDeliveryDays, dayCount, store.cart.meals, moduleConfig.ShowSkippedDays), [
        store.cart.daysOfWeek,
        store.model.Recipes,
        store.model.DeliveryDays,
        store.cart.startDate,
        store.cart.isSubscription,
        store.cart.subscriptionPeriod,
        store.cart.oneTimeOrderPeriod,
        store.model.GeneralSettings.OnlyShowDeliveryDays,
        moduleConfig.OnlySelectedDaysOfWeek,
        moduleConfig.OnlyDaysWithDefaultMeals,
        moduleConfig.ShowSkippedDays,
        dayCount,
    ]);
    const sliderSettings = setDayFilterSliderSettings(theme, moduleConfig.FilterLG, moduleConfig.FilterMD, moduleConfig.FilterSM, moduleConfig.FilterXS);
    const dayFilterFormat = moduleConfig.DayFilterFormat;
    const enableDaySkipping = moduleConfig.ShowSkippedDays && moduleConfig.EnableDaySkipping;
    const selectedDay = store.cart.selectedDay;
    const isAnyMealSkippedForDay = isAnyMealSkippedForGivenDay(store.cart.meals)(moment(selectedDay));
    const skipButtonText = createSkipDayButtonText(moduleConfig, isMobile, isAnyMealSkippedForDay, moment(selectedDay), tokenMap);
    const showCheckmarksForCompletedDays = (_d = moduleConfig.ShowCheckmarksForCompletedDays) !== null && _d !== void 0 ? _d : false;
    /**
     *  If the current selectedDay is not the first available day, set it to the first available day so meal selection always starts from the earliest available day.
     *  This can happen if the day filter is re-rendered with a different set of available days.
     */
    useEffect(() => {
        if (dayFilterDays.length > 0 && selectedDay) {
            const firstDay = dayFilterDays[0];
            if (!selectedDay.isSame(firstDay, "day")) {
                dispatch({ type: ActionTypes.SET_SELECTED_DAY, payload: firstDay });
            }
        }
    }, [dayFilterDays]);
    return {
        onlyDaysWithDefaultMeals,
        mealSelectionStatus,
        dayFilterDays,
        sliderSettings,
        dayFilterFormat,
        selectedDay,
        showCheckmarksForCompletedDays,
        isDayCompleted: isDayCompleted(store.cart.meals),
        isAnyMealSkippedForGivenDay: isAnyMealSkippedForGivenDay(store.cart.meals),
        handleSelectedDayChange: handleSelectedDayChange(selectedDay, dispatch),
        CreateDayFormattedLabel,
        toggleDaySkipped: toggleDaySkipped(dispatch, store.cart.meals, isAnyMealSkippedForDay),
        skipButtonText,
        enableDaySkipping,
    };
};
